import { useEffect, useState } from "react";
import useAuth from "./useAuth";
import useWebotServer from "./useWebotServer";
import { SALES_TRAINING_OWNER_EMAILS } from "../@types/sharedSchema";

// ---------------------------------------------------------------------------
// May this user open the sales onboarding portal v2 (/dashboard/admin/sales-training-v2)?
//
// The owners (SALES_TRAINING_OWNER_EMAILS: Haim, Yarden) always may. Everyone else is a reader only if an owner added
// their email from the portal page — a row on the server, so the sidebar has to ask (`GET .../me`, about the caller
// only). The answer is remembered in this browser for a few hours, so the question costs about one request per user
// per working day. This only decides whether the SIDEBAR shows the entry: the server checks every request itself.
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const cacheKey = (email: string) => `sales-training-v2:access:${email}`;

type CacheEntry = { canView: boolean; at: number };

const readCache = (email: string): CacheEntry | null => {
  try {
    const raw = window.localStorage.getItem(cacheKey(email));
    const parsed = raw ? (JSON.parse(raw) as CacheEntry) : null;
    return parsed && typeof parsed.canView === "boolean" && typeof parsed.at === "number" ? parsed : null;
  } catch {
    return null;
  }
};

/** The portal page tells the sidebar what the server just answered (e.g. after an owner added this user). */
export const rememberSalesTrainingV2Access = (email: string, canView: boolean): void => {
  try {
    if (email) window.localStorage.setItem(cacheKey(email), JSON.stringify({ canView, at: Date.now() }));
  } catch {
    // storage blocked: the sidebar just asks again next time
  }
};

export const isSalesTrainingV2OwnerEmail = (email: string): boolean =>
  (SALES_TRAINING_OWNER_EMAILS as readonly string[]).includes(email.toLowerCase());

export default function useSalesTrainingV2Access(): { canView: boolean; owner: boolean } {
  const { user } = useAuth();
  const { salesTrainingV2GetAccess } = useWebotServer();
  const email = (user?.email ?? "").toLowerCase();
  const owner = email ? isSalesTrainingV2OwnerEmail(email) : false;
  const [canView, setCanView] = useState<boolean>(() => owner || readCache(email)?.canView === true);

  useEffect(() => {
    if (!email) {
      setCanView(false);
      return undefined;
    }
    if (owner) {
      setCanView(true);
      return undefined;
    }
    const cached = readCache(email);
    if (cached) setCanView(cached.canView);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) return undefined;
    let cancelled = false;
    salesTrainingV2GetAccess()
      .then((access) => {
        if (cancelled) return;
        setCanView(access.canView);
        rememberSalesTrainingV2Access(email, access.canView);
      })
      .catch(() => {
        // offline or a server hiccup: keep whatever we knew, and ask again next time
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, owner]);

  return { canView: owner || canView, owner };
}
